import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MoreHoriz,
  Refresh,
  InsertDriveFileOutlined,
  AutoAwesome,
  TuneOutlined,
  Download
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { useGetKnowledgeAgentDocumentsQuery } from "../../data/api/queries/knowledgeAgent";
import { useStartPackageMutation } from "../../data/api/commands/knowledgeAgentCommands";
import {
  EngineeringDocument,
  StartPackageSection
} from "../../data/models/knowledge-agent.model";
import "./PackagingWorkspace.scss";

interface AnalysisParameters {
  assetType: string;
  gasType: string;
  compressorService: string;
  operatingPressure: string;
  operationalCriticality: string;
  facilityType: string;
  equipmentType: string;
}

const DISPLAY_CONFIG = {
  showContext: true,
  showSource: false
};

const ASSET_TYPES = ["FPSO", "Fixed Platform", "Onshore Facility"];
const GAS_TYPES = [
  { label: "Sweet Gas", value: "Sweet" },
  { label: "Sour Gas", value: "Sour" },
  { label: "Wet Gas", value: "Wet" },
  { label: "Dry Gas", value: "Dry" },
  { label: "Associated Gas", value: "Associated" },
  { label: "Non-associated Gas", value: "Non-associated" }
];
const COMPRESSOR_SERVICES = [
  "Gas Compression",
  "Gas Processing",
  "Oil Separation",
  "Produced Water Treatment",
  "Gas Lift",
  "Water Injection",
  "Flare & Vent",
  "Utilities (Fuel Gas / Air / Nitrogen)",
  "Export / Offloading"
];
const OPERATING_PRESSURES = [
  { label: "Low Pressure", value: "Low" },
  { label: "Medium Pressure", value: "Medium" },
  { label: "High Pressure", value: "High" },
  { label: "Ultra High Pressure", value: "Ultra High" }
];
const OPERATIONAL_CRITICALITIES = [
  "Production-critical",
  "Safety-critical",
  "Environment-critical",
  "Business-critical",
  "Non-critical"
];
const FACILITY_TYPES = [
  "Topside",
  "Subsea",
  "Hull",
  "Utilities Area",
  "Living Quarters"
];
const EQUIPMENT_TYPES = [
  "Compressor",
  "Pump",
  "Heat Exchanger",
  "Separator",
  "Hydrocyclone",
  "Flare Stack"
];

const PackagingWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const [startPackage] = useStartPackageMutation();

  const { data: documentsResponse, isLoading: isLoadingDocs } =
    useGetKnowledgeAgentDocumentsQuery();

  const engineeringDocs: EngineeringDocument[] = (
    documentsResponse?.data ?? []
  ).map((item) => ({
    id: item.documentGuid,
    documentName: item.name,
    date: new Date(item.createdDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    }),
    source: "Processing Hub"
  }));

  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [analysisParams, setAnalysisParams] = useState<AnalysisParameters>({
    assetType: "FPSO",
    gasType: "Sweet",
    compressorService: "Gas Compression",
    operatingPressure: "Low",
    operationalCriticality: "Production-critical",
    facilityType: "Topside",
    equipmentType: "Compressor"
    // assetType: ASSET_TYPES[0],
    // gasType: GAS_TYPES[0].label,
    // compressorService: COMPRESSOR_SERVICES[0],
    // operatingPressure: OPERATING_PRESSURES[0].label,
    // operationalCriticality: OPERATIONAL_CRITICALITIES[0]
  });
  const [activeTab, setActiveTab] = useState<"chat" | "prompts">("chat");
  const [chatInput, setChatInput] = useState("");
  const [packageResult, setPackageResult] = useState<{
    context: string;
    sections: StartPackageSection[];
    error?: string;
  } | null>(null);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);

  const handleDocToggle = (docId: string) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  const handleParamChange = (key: keyof AnalysisParameters, value: string) => {
    setAnalysisParams((prev) => ({ ...prev, [key]: value }));
  };

  const handleAnalyse = async () => {
    if (selectedDocs.size === 0) return;
    setIsAnalysing(true);
    setPackageResult(null);

    const payload = {
      message: {
        payload: {
          doc_ids: Array.from(selectedDocs),
          asset: analysisParams.assetType,
          service: analysisParams.compressorService,
          gas_type: analysisParams.gasType,
          pressure: analysisParams.operatingPressure,
          criticality: analysisParams.operationalCriticality
        }
      }
    };

    try {
      const result = await startPackage(payload).unwrap();

      if (result.status === "success" && result.data?.requirements?.sections) {
        setPackageResult({
          context: result.data.context || "",
          sections: result.data.requirements.sections
        });
      } else {
        setPackageResult({
          context: "",
          sections: [],
          error: "No requirements were generated. Please try again."
        });
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      setPackageResult({
        context: "",
        sections: [],
        error: `Error packaging documents: ${errorMessage}`
      });
    } finally {
      setIsAnalysing(false);
    }
  };

  const handleSendMessage = () => {
    if (!chatInput.trim()) return;
    setChatInput("");
  };

  const handleUploadBrowseClick = () => {
    uploadFileInputRef.current?.click();
  };

  const handleUploadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const handleUploadSubmit = () => {
    if (uploadFiles.length === 0) return;
    console.log("Uploading files:", uploadFiles);
    setUploadFiles([]);
    setIsUploadModalOpen(false);
  };

  const getSelectedDocNames = () => {
    return engineeringDocs
      .filter((doc) => selectedDocs.has(doc.id))
      .map((doc) => doc.documentName);
  };

  const handleDownloadPdf = () => {
    if (!packageResult || packageResult.error) return;

    // Sanitize text for jsPDF - remove non-ASCII/invisible Unicode chars
    const sanitize = (text: string): string => {
      return text
        .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, "") // zero-width & soft hyphen
        .replace(/[\u2018\u2019]/g, "'") // smart single quotes
        .replace(/[\u201C\u201D]/g, '"') // smart double quotes
        .replace(/[\u2013]/g, "-") // en dash
        .replace(/[\u2014]/g, "--") // em dash
        .replace(/[\u2026]/g, "...") // ellipsis
        .replace(/[\u2022]/g, "-") // bullet
        .replace(/[^\x20-\x7E\n]/g, ""); // strip remaining non-printable/non-ASCII
    };

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const maxWidth = pageWidth - margin * 2;
    let yPos = 20;

    const checkPageBreak = (requiredSpace: number) => {
      if (yPos + requiredSpace > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        yPos = 20;
      }
    };

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Engineering Package Report", margin, yPos);
    yPos += 12;

    // Selected Documents
    const selectedDocNames = getSelectedDocNames();
    if (selectedDocNames.length > 0) {
      checkPageBreak(20);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Selected Documents:", margin, yPos);
      yPos += 6;
      doc.setFont("helvetica", "normal");
      selectedDocNames.forEach((name) => {
        checkPageBreak(6);
        doc.text(sanitize(`- ${name}`), margin + 5, yPos);
        yPos += 5;
      });
      yPos += 5;
    }

    // Packaging Parameters
    checkPageBreak(50);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Packaging Parameters:", margin, yPos);
    yPos += 6;
    doc.setFont("helvetica", "normal");
    const gasLabel =
      GAS_TYPES.find((g) => g.value === analysisParams.gasType)?.label ||
      analysisParams.gasType;
    const pressureLabel =
      OPERATING_PRESSURES.find(
        (p) => p.value === analysisParams.operatingPressure
      )?.label || analysisParams.operatingPressure;
    const params = [
      `Asset Type: ${analysisParams.assetType}`,
      `Gas Type: ${gasLabel}`,
      `Service: ${analysisParams.compressorService}`,
      `Operating Pressure: ${pressureLabel}`,
      `Operational Criticality: ${analysisParams.operationalCriticality}`,
      `Facility Type: ${analysisParams.facilityType}`,
      `Equipment Type: ${analysisParams.equipmentType}`
    ];
    params.forEach((param) => {
      checkPageBreak(6);
      doc.text(sanitize(param), margin + 5, yPos);
      yPos += 5;
    });
    yPos += 8;

    if (packageResult.context) {
      checkPageBreak(20);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Context:", margin, yPos);
      yPos += 6;
      doc.setFont("helvetica", "normal");
      const contextLines = doc.splitTextToSize(
        sanitize(packageResult.context),
        maxWidth
      );
      doc.text(contextLines, margin, yPos);
      yPos += contextLines.length * 5 + 8;
    }

    packageResult.sections.forEach((section, sIdx) => {
      checkPageBreak(15);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(sanitize(`${sIdx + 1}. ${section.title}`), margin, yPos);
      yPos += 8;

      section.requirements.forEach((req) => {
        checkPageBreak(10);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(
          sanitize(`- ${req.text}`),
          maxWidth - 5
        );
        doc.text(lines, margin + 5, yPos);
        yPos += lines.length * 5 + 3;
      });

      yPos += 5;
    });

    doc.save("engineering-package-report.pdf");
  };

  const handleDownloadExcel = () => {
    if (!packageResult || packageResult.error) return;

    const selectedDocNames = getSelectedDocNames();
    const gasLabel =
      GAS_TYPES.find((g) => g.value === analysisParams.gasType)?.label ||
      analysisParams.gasType;
    const pressureLabel =
      OPERATING_PRESSURES.find(
        (p) => p.value === analysisParams.operatingPressure
      )?.label || analysisParams.operatingPressure;

    const rows: Record<string, string>[] = [];
    let isFirstRow = true;

    packageResult.sections.forEach((section, sIdx) => {
      let isFirstInSection = true;
      section.requirements.forEach((req) => {
        if (isFirstRow) {
          rows.push({
            "Selected Documents": selectedDocNames.join(", "),
            "Asset Type": analysisParams.assetType,
            "Gas Type": gasLabel,
            Service: analysisParams.compressorService,
            "Operating Pressure": pressureLabel,
            "Operational Criticality": analysisParams.operationalCriticality,
            "Facility Type": analysisParams.facilityType,
            "Equipment Type": analysisParams.equipmentType,
            Section: `${sIdx + 1}. ${section.title}`,
            Requirement: req.text
          });
          isFirstRow = false;
          isFirstInSection = false;
        } else {
          rows.push({
            "Selected Documents": "",
            "Asset Type": "",
            "Gas Type": "",
            Service: "",
            "Operating Pressure": "",
            "Operational Criticality": "",
            "Facility Type": "",
            "Equipment Type": "",
            Section: isFirstInSection ? `${sIdx + 1}. ${section.title}` : "",
            Requirement: req.text
          });
          isFirstInSection = false;
        }
      });
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Engineering Package");

    XLSX.writeFile(wb, "engineering-package-report.xlsx");
  };

  return (
    <div className="packaging-workspace-page">
      {/* Breadcrumb */}
      <div className="hub-breadcrumb">
        <span className="breadcrumb-link" onClick={() => navigate("/")}>
          Home
        </span>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Packaging</span>
      </div>

      <h1 className="page-title">Engineering Packaging Workspace</h1>
      <p className="page-description">
        Generate and assemble AI-assisted engineering package configurations.
      </p>

      {/* Engineering Document Panel */}
      <div className="panel">
        <div className="panel-header">
          <div className="panel-header-content">
            <h2 className="panel-title">Engineering Document</h2>
            <p className="panel-subtitle">
              Select engineering specification for Packaging
            </p>
          </div>
          {/* <button
            className="upload-btn"
            onClick={() => setIsUploadModalOpen(true)}
          >
            <Upload className="upload-icon" />
            Upload
          </button> */}
        </div>

        <div className="panel-table-wrapper">
          <table className="panel-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Document Name</th>
                <th>Date</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingDocs ? (
                <tr>
                  <td colSpan={4}>Loading documents...</td>
                </tr>
              ) : (
                engineeringDocs.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedDocs.has(doc.id)}
                        onChange={() => handleDocToggle(doc.id)}
                      />
                    </td>
                    <td>
                      <span className="doc-link" title={doc.documentName}>
                        {doc.documentName}
                      </span>
                    </td>
                    <td>{doc.date}</td>
                    <td>{doc.source}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Analysis Parameters */}
      <div className="analysis-parameters">
        <div className="parameters-header">
          <TuneOutlined className="parameters-icon" />
          <h2 className="parameters-title">Packaging Parameters</h2>
        </div>
        <div className="parameters-grid">
          <div className="parameter-field">
            <label className="parameter-label">Asset Type</label>
            <select
              className="parameter-select"
              value={analysisParams.assetType}
              onChange={(e) => handleParamChange("assetType", e.target.value)}
            >
              {ASSET_TYPES.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="parameter-field">
            <label className="parameter-label">Gas type</label>
            <select
              className="parameter-select"
              value={analysisParams.gasType}
              onChange={(e) => handleParamChange("gasType", e.target.value)}
            >
              {GAS_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="parameter-field">
            <label className="parameter-label">Service</label>
            <select
              className="parameter-select"
              value={analysisParams.compressorService}
              onChange={(e) =>
                handleParamChange("compressorService", e.target.value)
              }
            >
              {COMPRESSOR_SERVICES.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="parameter-field">
            <label className="parameter-label">Operating Pressure</label>
            <select
              className="parameter-select"
              value={analysisParams.operatingPressure}
              onChange={(e) =>
                handleParamChange("operatingPressure", e.target.value)
              }
            >
              {OPERATING_PRESSURES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="parameter-field">
            <label className="parameter-label">Operational Criticality</label>
            <select
              className="parameter-select"
              value={analysisParams.operationalCriticality}
              onChange={(e) =>
                handleParamChange("operationalCriticality", e.target.value)
              }
            >
              {OPERATIONAL_CRITICALITIES.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="parameter-field">
            <label className="parameter-label">Facility Type</label>
            <select
              className="parameter-select"
              value={analysisParams.facilityType}
              onChange={(e) =>
                handleParamChange("facilityType", e.target.value)
              }
            >
              {FACILITY_TYPES.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="parameter-field">
            <label className="parameter-label">Equipment Type</label>
            <select
              className="parameter-select"
              value={analysisParams.equipmentType}
              onChange={(e) =>
                handleParamChange("equipmentType", e.target.value)
              }
            >
              {EQUIPMENT_TYPES.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      {selectedDocs.size > 0 && (
        <div className="action-bar">
          <div className="action-bar-left">
            <div className="action-bar-info">
              <div className="ready-label">READY TO PACKAGE</div>
              <div className="selected-count">
                {selectedDocs.size} Documents
                <span> Selected</span>
              </div>
            </div>
            <div className="selected-chips">
              {getSelectedDocNames().map((name, idx) => (
                <span key={idx} className="chip">
                  <InsertDriveFileOutlined
                    style={{ fontSize: 14, marginRight: 4 }}
                  />
                  {name}
                </span>
              ))}
            </div>
          </div>
          <div className="action-bar-right">
            <button
              className="analyse-btn"
              onClick={handleAnalyse}
              disabled={selectedDocs.size === 0}
            >
              Create Package
              <TuneOutlined className="analyse-icon" />
            </button>
          </div>
        </div>
      )}

      {/* AI Chat Analysis Section */}
      {selectedDocs.size > 0 && (
        <div className="analysis-chat-section">
          {/* <div className="chat-tabs-header">
            <div className="chat-tabs">
              <button
                className={`chat-tab ${activeTab === "chat" ? "active" : ""}`}
                onClick={() => setActiveTab("chat")}
              >
                <AutoAwesome className="tab-icon" />
                AI Chat
              </button>
              <button
                className={`chat-tab ${
                  activeTab === "prompts" ? "active" : ""
                }`}
                onClick={() => setActiveTab("prompts")}
              >
                Prompts
              </button>
            </div>
            <div className="chat-header-actions">
              <button className="chat-action-btn" title="Refresh">
                <Refresh fontSize="small" />
              </button>
              <button className="chat-action-btn" title="More options">
                <MoreHoriz fontSize="small" />
              </button>
            </div>
          </div> */}

          <div className="chat-body">
            <div className="chat-messages">
              {isAnalysing ? (
                <div className="chat-message assistant">
                  <div className="message-content">
                    <div className="analysis-loading">
                      <CircularProgress size={40} />
                      <p>Packaging documents... Please wait.</p>
                    </div>
                  </div>
                </div>
              ) : packageResult ? (
                <>
                  <div className="chat-message assistant">
                    <div className="message-content">
                      {packageResult.error ? (
                        <p className="section-line">{packageResult.error}</p>
                      ) : (
                        <>
                          {DISPLAY_CONFIG.showContext &&
                            packageResult.context && (
                              <div className="response-context">
                                <span className="context-label">Context</span>
                                <p className="context-text">
                                  {packageResult.context}
                                </p>
                              </div>
                            )}
                          {packageResult.sections.map((section, sIdx) => (
                            <div key={sIdx} className="response-section">
                              <h4 className="section-title">
                                <span className="section-number">
                                  {sIdx + 1}
                                </span>
                                {section.title}
                              </h4>
                              <ul className="requirements-list">
                                {section.requirements.map((req, rIdx) => (
                                  <React.Fragment key={rIdx}>
                                    <li className="requirement-item">
                                      {req.text}
                                    </li>
                                    {DISPLAY_CONFIG.showSource &&
                                      req.source && (
                                        <li className="source-ref">
                                          <span className="source-badge">
                                            Source
                                          </span>
                                          <span className="source-id">
                                            {req.source}
                                          </span>
                                        </li>
                                      )}
                                  </React.Fragment>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                  {!packageResult.error && (
                    <div className="download-btn-container">
                      <button
                        className="download-pdf-btn"
                        onClick={handleDownloadPdf}
                      >
                        {/* <Download style={{ fontSize: 16 }} /> */}
                        <PictureAsPdfOutlinedIcon style={{ fontSize: 16 }} />
                        Download PDF
                      </button>
                      <button
                        className="download-pdf-btn"
                        onClick={handleDownloadExcel}
                      >
                        <img
                          src={require("../../../../assets/media/icons/custom/excel_icon.png")}
                          alt="Excel"
                          style={{
                            width: 16,
                            height: 16,
                            filter: "brightness(0) invert(1)",
                            imageRendering: "crisp-edges"
                          }}
                        />
                        Download Excel
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>

          {/* <div className="chat-footer">
            <div className="chat-input-container">
              <input
                type="text"
                className="chat-input"
                placeholder="Ask follow-up packaging questions..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
              />
              <button className="chat-send-btn" onClick={handleSendMessage}>
                <Send fontSize="small" />
              </button>
            </div>
            <p className="chat-disclaimer">
              Exercise professional judgement while reviewing AI responses as
              they may be inaccurate.
            </p>
          </div> */}
        </div>
      )}

      {/* Upload Modal */}
      {/* {isUploadModalOpen && (
        <div
          className="upload-modal-overlay"
          onClick={() => setIsUploadModalOpen(false)}
        >
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h3>Upload Engineering Documents</h3>
              <button
                className="modal-close-btn"
                onClick={() => setIsUploadModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <div className="upload-modal-body">
              <div
                className={`upload-dropzone${
                  uploadFiles.length > 0 ? " has-files" : ""
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add("drag-over");
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove("drag-over");
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove("drag-over");
                  const droppedFiles = Array.from(e.dataTransfer.files).filter(
                    (file) => file.size > 0 || file.type !== ""
                  );
                  if (droppedFiles.length > 0) {
                    setUploadFiles((prev) => [...prev, ...droppedFiles]);
                  }
                }}
                onClick={handleUploadBrowseClick}
              >
                <div className="dropzone-icon">
                  <CloudUpload className="cloud-upload-icon" />
                </div>
                <p className="dropzone-text">
                  Drag and drop files here or{" "}
                  <span className="browse-link">browse</span>
                </p>
                <p className="dropzone-max-size">
                  Supported: .pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt
                </p>
              </div>

              <button
                className="upload-attachment-btn"
                onClick={handleUploadSubmit}
                disabled={uploadFiles.length === 0}
              >
                <UploadOutlined style={{ fontSize: 18, marginRight: 6 }} />
                Upload Attachment
              </button>

              <input
                ref={uploadFileInputRef}
                type="file"
                multiple
                onChange={handleUploadFileChange}
                style={{ display: "none" }}
              />

              {uploadFiles.length > 0 && (
                <div className="selected-files-list">
                  <p>{uploadFiles.length} file(s) selected</p>
                  {uploadFiles.map((file, idx) => (
                    <div key={idx} className="selected-file-item">
                      <InsertDriveFileOutlined style={{ fontSize: 16 }} />
                      <span>{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default PackagingWorkspace;
